import * as React from "react"
import type { IntroNodes, Tone } from "./context"

const LOGO = "https://framerusercontent.com/images/ahG16CVY3IOadZujceXG7DY7LrE.svg"
const INK: Record<Tone, number> = { home: 23, dark: 255, light: 0 }
/** logo filter per tone: [brightness, invert] */
const LOGO_FILTER: Record<Tone, [number, number]> = { home: [1, 0], dark: [0, 1], light: [0, 0] }

export type HeaderApi = {
    /** to a work page: the colour follows the next page's background as it comes up, d = 0..1 */
    mix: (to: Tone, d: number) => void
    /** show a tone; fade = the 0.6s colour fade used between work pages */
    tone: (tone: Tone, fade?: boolean) => void
    /** leaving for the home: logo and local time fade out */
    fadeOut: () => void
    /** the home intro is about to animate every part in */
    prepareIntro: () => void
}

function useClock() {
    const fmt = () =>
        new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" }).format(new Date())
    const [time, setTime] = React.useState(fmt)
    React.useEffect(() => {
        const id = window.setInterval(() => setTime(fmt()), 15000)
        return () => window.clearInterval(id)
    }, [])
    return time
}

/** Stop finished entrances that hold opacity, then fade to 0. */
export function fadeAway(el: HTMLElement, ms = 450) {
    for (const a of el.getAnimations()) {
        try {
            a.commitStyles()
        } catch {}
        a.cancel()
    }
    void getComputedStyle(el).opacity
    el.style.transition = `opacity ${ms}ms cubic-bezier(.5,0,.5,1)`
    el.style.opacity = "0"
}

type Props = {
    initialTone: Tone
    /** the first page is the home: everything starts hidden for its intro */
    introFirst: boolean
    /** first page is a work page: fade in */
    enter: boolean
    mobile: boolean
    homeHref: string
    onHome: (e: React.MouseEvent<HTMLAnchorElement>) => void
    intro: React.MutableRefObject<IntroNodes>
}

export const Header = React.forwardRef<HeaderApi, Props>(function Header(
    { initialTone, introFirst, enter, mobile, homeHref, onHome, intro },
    api
) {
    const root = React.useRef<HTMLElement>(null)
    const logo = React.useRef<HTMLImageElement>(null)
    const time = useClock()
    const hidden = introFirst ? { opacity: 0 } : undefined
    const node = (key: keyof IntroNodes) => (el: HTMLElement | null) => {
        if (el) intro.current[key] = el
    }

    React.useImperativeHandle(api, () => {
        const el = () => root.current!
        const setTone = (t: Tone) => el().setAttribute("data-tone", t)
        return {
            mix(to, d) {
                const h = el()
                const from = (h.getAttribute("data-tone") as Tone) || "home"
                const v = Math.round(INK[from] + (INK[to] - INK[from]) * d)
                h.style.color = `rgb(${v},${v},${v})`
                // the logo is drawn dark: brightness(0) makes it black, invert(1) white
                const [b0, i0] = LOGO_FILTER[from],
                    [b1, i1] = LOGO_FILTER[to]
                const f = (n: number) => n.toFixed(3)
                if (logo.current) logo.current.style.filter = `brightness(${f(b0 + (b1 - b0) * d)}) invert(${f(i0 + (i1 - i0) * d)})`
            },
            tone(t, fade = false) {
                const h = el()
                if (fade) {
                    h.setAttribute("data-fade", "")
                    window.setTimeout(() => h.removeAttribute("data-fade"), 1000)
                } else h.removeAttribute("data-fade")
                h.style.color = ""
                if (logo.current) logo.current.style.filter = ""
                setTone(t)
            },
            fadeOut() {
                fadeAway(el())
            },
            prepareIntro() {
                const h = el()
                h.removeAttribute("data-enter")
                h.style.transition = "none"
                h.style.opacity = "1"
                for (const k of ["logo", "signature0", "signature1", "time0", "time1"] as const) {
                    const n = intro.current[k]
                    if (n) n.style.opacity = "0"
                }
                setTone("home")
            },
        }
    }, [])

    return (
        <header
            ref={root}
            className="sh-header"
            data-tone={initialTone}
            data-mobile={mobile ? "true" : "false"}
            data-enter={enter ? "true" : undefined}
        >
            <div className="sh-clock">
                <div>
                    <span ref={node("time0")} style={hidden}>
                        Local time
                    </span>
                </div>
                <div>
                    <span ref={node("time1")} style={hidden}>
                        {time}
                    </span>
                </div>
            </div>
            <a className="sh-brand" href={homeHref} aria-label="NTO home" onClick={onHome}>
                <img ref={(el) => ((logo as any).current = el) && node("logo")(el)} className="sh-logo" src={LOGO} alt="NTO" style={hidden} />
                <span className="sh-sig">
                    <span>
                        <span ref={node("signature0")} style={hidden}>
                            The design office
                        </span>
                    </span>
                    <span>
                        <span ref={node("signature1")} style={hidden}>
                            of Tiago Nascimento
                        </span>
                    </span>
                </span>
            </a>
        </header>
    )
})
